import { Link } from '@inertiajs/react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/hooks/use-translations';

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

interface PaginationProps {
    links: PaginationLink[];
    currentPage: number;
    lastPage: number;
    className?: string;
}

export function Pagination({ links, currentPage, lastPage, className = '' }: PaginationProps) {
    const { t } = useTranslations();
    
    // Don't show pagination if there's only one page
    if (lastPage <= 1) {
        return null;
    }

    const getVisiblePages = () => {
        const delta = 2; // Number of pages to show on each side of current page
        const range = [];
        const rangeWithDots = [];

        // Calculate range of pages to show
        const start = Math.max(2, currentPage - delta);
        const end = Math.min(lastPage - 1, currentPage + delta);

        for (let i = start; i <= end; i++) {
            range.push(i);
        }

        // Add first page
        if (start > 2) {
            rangeWithDots.push(1);
            if (start > 3) {
                rangeWithDots.push('...');
            }
        } else if (start === 2) {
            rangeWithDots.push(1);
        }

        // Add range
        rangeWithDots.push(...range);

        // Add last page
        if (end < lastPage - 1) {
            if (end < lastPage - 2) {
                rangeWithDots.push('...');
            }
            rangeWithDots.push(lastPage);
        } else if (end === lastPage - 1) {
            rangeWithDots.push(lastPage);
        }

        return rangeWithDots;
    };

    const visiblePages = getVisiblePages();
    const prevLink = links.find(link => link.label.includes('Previous') || link.label.includes('&laquo;'));
    const nextLink = links.find(link => link.label.includes('Next') || link.label.includes('&raquo;'));

    return (
        <div className={`flex items-center justify-center space-x-1 ${className}`}>
            {/* Previous Button */}
            <Button
                variant="outline"
                size="sm"
                disabled={!prevLink?.url}
                asChild={!!prevLink?.url}
            >
                {prevLink?.url ? (
                    <Link href={prevLink.url} className="flex items-center gap-1">
                        <ChevronLeft className="h-4 w-4" />
                        <span>{t('Previous')}</span>
                    </Link>
                ) : (
                    <div className="flex items-center gap-1">
                        <ChevronLeft className="h-4 w-4" />
                        <span>{t('Previous')}</span>
                    </div>
                )}
            </Button>

            {/* Page Numbers */}
            {visiblePages.map((page, index) => {
                if (page === '...') {
                    return (
                        <div key={`dots-${index}`} className="px-3 py-2">
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </div>
                    );
                }

                const pageLink = links.find(link => link.label === String(page));
                const isActive = page === currentPage;

                return (
                    <Button
                        key={page}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        disabled={!pageLink?.url}
                        asChild={!!pageLink?.url}
                    >
                        {pageLink?.url ? (
                            <Link href={pageLink.url}>
                                {page}
                            </Link>
                        ) : (
                            <span>{page}</span>
                        )}
                    </Button>
                );
            })}

            {/* Next Button */}
            <Button
                variant="outline"
                size="sm"
                disabled={!nextLink?.url}
                asChild={!!nextLink?.url}
            >
                {nextLink?.url ? (
                    <Link href={nextLink.url} className="flex items-center gap-1">
                        <span>{t('Next')}</span>
                        <ChevronRight className="h-4 w-4" />
                    </Link>
                ) : (
                    <div className="flex items-center gap-1">
                        <span>{t('Next')}</span>
                        <ChevronRight className="h-4 w-4" />
                    </div>
                )}
            </Button>
        </div>
    );
}